"use client";

import React, { useState } from "react";
import { Compass, FileCheck2, Landmark, Layers as Layers3, Calculator, Building, CircleCheck as CheckCircle2, ArrowRight, Info, Clock, Hammer } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function RemodelPlanningGuide() {
  const [activeSection, setActiveSection] = useState<string>("scope");
  const guideSections = [{
    id: "scope",
    number: "01",
    title: "Defining Scope & Establishing Priorities",
    subtitle: "Separating non-negotiable structural needs from aesthetic upgrades",
    icon: Compass,
    content: <div className="space-y-4 text-foreground/90 leading-relaxed text-sm sm:text-base">
          <p>
            Every successful renovation in Decatur begins with a clearly articulated scope of work. 
            Before drawing elevations or selecting tile finishes, establish whether your project is an 
            isolated room update, a reconfiguration of the existing building footprint, or an addition that expands your conditioned living area.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 my-4">
            <div className="p-4 rounded-lg bg-secondary/60 border border-border">
              <h4 className="font-serif font-semibold text-foreground text-base mb-1.5 flex items-center gap-2">
                <CheckCircle2 className="size-4 text-chart-1 shrink-0" />
                Essential Core Needs
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Upgrading outdated 100-amp electrical panels, replacing galvanized supply lines, insulating uninsulated crawlspaces, or correcting unlevel subfloors.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-secondary/60 border border-border">
              <h4 className="font-serif font-semibold text-foreground text-base mb-1.5 flex items-center gap-2">
                <CheckCircle2 className="size-4 text-chart-1 shrink-0" />
                Lifestyle & Aesthetic Goals
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Opening an enclosed kitchen to dining spaces, adding double vanities to a master suite, installing custom millwork, or creating covered rear porch connections.
              </p>
            </div>
          </div>
          <p>
            Documenting must-haves versus nice-to-haves early allows your design and building team 
            to produce value engineering options without compromising the foundational integrity or primary functionality of your home.
          </p>
        </div>
  }, {
    id: "budgeting",
    number: "02",
    title: "Budgeting Approach & Contingency Frameworks",
    subtitle: "Structuring material allowances and contingency buffers for older homes",
    icon: Calculator,
    content: <div className="space-y-4 text-foreground/90 leading-relaxed text-sm sm:text-base">
          <p>
            A realistic remodel budget encompasses three core pillars: hard construction costs (framing, MEP systems, finishes), soft professional costs (architectural drawings, site surveys, engineering calculations), and an essential contingency allocation.
          </p>
          <div className="p-4 sm:p-5 rounded-xl bg-card border border-border/80 shadow-xs space-y-3">
            <h4 className="font-serif font-semibold text-foreground text-base">The Older Home Contingency Rule</h4>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              When working with Decatur homes built between 1920 and 1965, always maintain an unallocated contingency reserve of 15% to 20% beyond the contract base. Older framing, plaster-over-lathe walls, and concealed plumbing frequently reveal unforeseen conditions once demolition opens existing assemblies.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-xs">
              <div className="bg-secondary/40 p-2.5 rounded border border-border/50">
                <span className="font-medium text-foreground block mb-0.5">Demolition & Substructure</span>
                <span className="text-muted-foreground">Foundation settlement, joist rot, crawlspace moisture</span>
              </div>
              <div className="bg-secondary/40 p-2.5 rounded border border-border/50">
                <span className="font-medium text-foreground block mb-0.5">MEP Infrastructure</span>
                <span className="text-muted-foreground">Cast iron drains, knob-and-tube wiring, supply duct sizing</span>
              </div>
              <div className="bg-secondary/40 p-2.5 rounded border border-border/50">
                <span className="font-medium text-foreground block mb-0.5">Allowances & Finishes</span>
                <span className="text-muted-foreground">Solid wood flooring integration, specialized stone fabrication</span>
              </div>
            </div>
          </div>
          <p>
            Aligning material selections and finish packages early in pre-construction prevents costly change orders during active framing and trade installation.
          </p>
        </div>
  }, {
    id: "permitting",
    number: "03",
    title: "Permitting Steps: City of Decatur vs. DeKalb County",
    subtitle: "Navigating distinct municipal jurisdictions, tree ordinances, and zoning codes",
    icon: FileCheck2,
    content: <div className="space-y-4 text-foreground/90 leading-relaxed text-sm sm:text-base">
          <p>
            Jurisdiction determines your permit timeline, plan review requirements, and zoning envelope restrictions. Decatur properties fall either within the incorporated City of Decatur limits or unincorporated DeKalb County.
          </p>
          <div className="grid md:grid-cols-2 gap-4 my-2">
            <div className="p-4 rounded-xl bg-secondary/50 border border-border space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-serif font-semibold text-foreground">City of Decatur</h4>
                <Badge variant="outline" className="text-xs">Municipal Code</Badge>
              </div>
              <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground list-disc pl-4">
                <li>Strict tree canopy preservation ordinance with mandatory arborist review for ground disturbances.</li>
                <li>Comprehensive floor area ratio (FAR) and impervious lot coverage maximum calculations.</li>
                <li>Single-point plan intake via Decatur Design, Environment & Construction division.</li>
                <li>Mandatory stormwater mitigation calculations for footprint additions exceeding specified limits.</li>
              </ul>
            </div>
            <div className="p-4 rounded-xl bg-secondary/50 border border-border space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="font-serif font-semibold text-foreground">Unincorporated DeKalb</h4>
                <Badge variant="outline" className="text-xs">County Code</Badge>
              </div>
              <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground list-disc pl-4">
                <li>Permit routing through DeKalb County Planning & Sustainability Department.</li>
                <li>Standard county setback and zoning compliance reviews based on underlying zoning district.</li>
                <li>Health Department septic or watershed approvals where sewer lines or stream buffers apply.</li>
                <li>Separate trade affidavits required for electrical, mechanical, and master plumbing permits.</li>
              </ul>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Plan submission packages typically require a boundary and topographic survey, architectural dimensioned floor plans, structural framing details, and energy code compliance forms (Georgia Energy Code).
          </p>
        </div>
  }, {
    id: "historic",
    number: "04",
    title: "Historic District Design Review & Guidelines",
    subtitle: "Navigating Certificates of Appropriateness (COA) for designated neighborhoods",
    icon: Landmark,
    content: <div className="space-y-4 text-foreground/90 leading-relaxed text-sm sm:text-base">
          <p>
            If your property is situated within designated local historic districts—such as McDonough-Adams-Kings Highway (MAK), Clairemont Avenue, Old Decatur, or Ponce de Leon Court—exterior modifications require formal review before standard building permits can be issued.
          </p>
          <div className="p-4 rounded-xl bg-card border border-border/80 shadow-xs space-y-3">
            <h4 className="font-serif font-semibold text-foreground text-base flex items-center gap-2">
              <Clock className="size-4 text-chart-1" />
              Historic Preservation Commission (HPC) Timeline
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Obtaining a Certificate of Appropriateness (COA) requires submitting scaled architectural elevations, material specification sheets, window schedules, and site context photographs. Applications must be submitted approximately 3 to 4 weeks prior to the scheduled monthly public hearing.
            </p>
            <div className="space-y-1.5 pt-1 text-xs sm:text-sm text-foreground/80">
              <div className="flex items-start gap-2">
                <span className="font-semibold text-chart-1">•</span>
                <span><strong>Window Replacements:</strong> Preservation of original wood sash patterns and true divided light profiles are prioritized over generic vinyl inserts.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-chart-1">•</span>
                <span><strong>Additions & Rooflines:</strong> New additions must remain subordinate in scale and massing to the primary historic facade and maintain matching roof pitches.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="font-semibold text-chart-1">•</span>
                <span><strong>Porch Architecture:</strong> Retaining historic columns, beadboard soffits, and traditional wood decking profiles preserves authentic neighborhood character.</span>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: "sequencing",
    number: "05",
    title: "Sequencing Considerations for Classic Architectural Styles",
    subtitle: "Tailoring project milestones to 1920s Bungalows, Craftsman homes, and mid-century Brick Ranches",
    icon: Layers3,
    content: <div className="space-y-4 text-foreground/90 leading-relaxed text-sm sm:text-base">
          <p>
            Decatur is celebrated for its rich architectural diversity. The chronological sequencing of structural interventions varies significantly depending on your home’s era and structural makeup.
          </p>
          <div className="grid gap-3 my-2">
            <div className="p-4 rounded-lg bg-secondary/40 border border-border">
              <h4 className="font-serif font-semibold text-foreground text-sm sm:text-base mb-1">
                1920s Craftsman & Folk Victorian Bungalows
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Prioritize structural crawlspace leveling, foundation pier stabilization, and joist sistering before drywalling or setting cabinetry. Integrate continuous vapour barriers to prevent rising humidity from damaging historic heart-pine flooring.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-secondary/40 border border-border">
              <h4 className="font-serif font-semibold text-foreground text-sm sm:text-base mb-1">
                1950s–1960s Mid-Century Brick Ranches
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Ranch layouts often feature load-bearing center spine walls. Removing these partitions to establish open kitchen-living concepts requires installing flush engineered LVL or steel beams, along with verifying adequate point-load transfers down to basement footings.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-secondary/40 border border-border">
              <h4 className="font-serif font-semibold text-foreground text-sm sm:text-base mb-1">
                Second-Story Additions & Dormer Pop-Ups
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Adding second-floor living space necessitates geotechnical soil checks, structural footing verification, and precise sequencing to keep the main level weather-tight during roof removal and upper-level framing.
              </p>
            </div>
          </div>
        </div>
  }];
  return <section id="remodelplanningguide" data-nav="light" className="py-16 sm:py-24 bg-background text-foreground relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px w-8 bg-chart-1"></span>
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Planning & Pre-Construction Roadmap
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground text-balance">
            How to plan a home renovation in Decatur
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A comprehensive guide covering scope definition, budgeting models, municipal zoning rules, historic preservation review, and construction sequencing for older neighborhood homes.
          </p>
        </div>

        {/* Layout: Sidebar Jump Menu + Content Cards */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Quick Jump Rail / Navigation for desktop */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 space-y-4">
            <div className="p-5 rounded-2xl bg-card/90 border border-border shadow-xs backdrop-blur-xs">
              <div className="flex items-center gap-2 pb-3 mb-3 border-b border-border/70">
                <Hammer className="size-4 text-chart-1" />
                <h3 className="font-serif font-semibold text-foreground text-sm">
                  Planning Phases
                </h3>
              </div>
              <nav aria-label="Planning Guide Sections" className="space-y-1">
                {guideSections.map((sec, idx) => {
                const Icon = sec.icon;
                const isActive = activeSection === sec.id;
                return <Button key={sec.id} type="button" variant="ghost" data-index={idx} onClick={() => {
                  setActiveSection(sec.id);
                  const el = document.getElementById(`guide-step-${sec.id}`);
                  if (el) {
                    el.scrollIntoView({
                      behavior: "smooth",
                      block: "start"
                    });
                  }
                }} className={`w-full h-auto text-left px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-between group ${isActive ? "bg-secondary text-foreground font-semibold shadow-2xs hover:bg-secondary hover:text-foreground" : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"}`}>
                      <div className="flex items-center gap-2.5 truncate">
                        <span className="font-mono text-xs text-muted-foreground/80">{sec.number}</span>
                        <span className="truncate">{sec.title.split("&")[0]}</span>
                      </div>
                      <Icon className={`size-3.5 shrink-0 transition-transform ${isActive ? "text-chart-1" : "text-muted-foreground group-hover:translate-x-0.5"}`} />
                    </Button>;
              })}
              </nav>

              <Separator className="my-4" />

              <div className="bg-secondary/40 p-3.5 rounded-xl border border-border/60">
                <div className="flex items-start gap-2.5">
                  <Info className="size-4 text-chart-1 shrink-0 mt-0.5" />
                  <div className="text-xs text-muted-foreground leading-snug">
                    <p className="font-medium text-foreground mb-0.5">Need a feasibility check?</p>
                    Discuss your project boundaries, zoning constraints, and spatial goals with our planning team.
                  </div>
                </div>
                <div className="mt-3">
                  <Button asChild size="sm" variant="default" className="w-full text-xs">
                    <Link to="/ContactPage">
                      Contact our team
                      <ArrowRight className="size-3 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </aside>

          {/* Detailed Content Stream */}
          <div className="lg:col-span-8 space-y-8">
            {guideSections.map((sec, idx) => {
            const Icon = sec.icon;
            return <article key={sec.id} id={`guide-step-${sec.id}`} data-index={idx} className="scroll-mt-24">
                  <Card className="bg-card/90 border border-border/80 shadow-sm rounded-2xl overflow-hidden">
                    <CardHeader className="p-6 sm:p-8 pb-4 border-b border-border/40 bg-secondary/20">
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-background border border-border text-xs font-mono font-medium text-muted-foreground">
                          <span>Phase {sec.number}</span>
                        </div>
                        <div className="size-9 rounded-lg bg-card border border-border flex items-center justify-center text-chart-1 shadow-2xs">
                          <Icon className="size-5" />
                        </div>
                      </div>
                      <CardTitle className="font-serif text-xl sm:text-2xl font-normal text-foreground leading-tight">
                        {sec.title}
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {sec.subtitle}
                      </p>
                    </CardHeader>
                    <CardContent className="p-6 sm:p-8">
                      {sec.content}
                    </CardContent>
                  </Card>
                </article>;
          })}

            {/* Bottom Callout Banner */}
            <div className="p-6 sm:p-8 rounded-2xl bg-secondary/80 border border-border shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="space-y-1 max-w-lg">
                <h3 className="font-serif text-xl font-normal text-foreground">
                  Ready to evaluate your home's layout?
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Explore our dedicated project gallery or connect with our design team to review site feasibility and architectural options.
                </p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
                <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                  <Link to="/ProjectsPage">View projects</Link>
                </Button>
                <Button asChild size="sm" className="w-full sm:w-auto">
                  <Link to="/ContactPage">Start planning</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}